import { themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import images from "../../../assets/images";
import Modal from "../../../components/modal/Modal";
import {
  useDisposalManagementStore,
  useHazardousMaterialStore,
} from "../../../store/hazardousMaterialStore";
import { DisposalEventType } from "../../../types/api";
import {
  useDisposalMethodsListStore,
  useUnitsofMeasureStore,
} from "../../../store/masterDataStore";
import { useOrgUsersStore } from "../../../store/store";
import { OrgUserType } from "../../../types/api";
import { useS3Upload, S3TempUrl } from "../../../utils/s3Client";
import FileUpload from "../../../components/FileUpload/FileUpload";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";
import DocumentViewer from "../../../components/DocumentViewer/DocumentViewer";
import PdfViewer from "../../../components/pdf/PdfViewer";

const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgba(0, 0, 0, 0.03)",
  headerColumnResizeHandleColor: "rgb(126, 46, 132)",
  fontSize: "0.75rem",
  headerFontWeight: "bold",
});

const emptyForm: DisposalEventType = {
  disposalId: "",
  disposalDateTime: "",
  materialName: "",
  materialId: "",
  quantityDisposed: "",
  unitOfMeasure: "",
  sourceLocation: "",
  reasonForDisposal: "",
  conditionBeforeDisposal: "",
  disposalMethod: "",
  disposalVendor: "",
  transportMethod: "",
  authorizedBy: "",
  complianceReference: "",
  attachment: "",
  witnessedBy: "",
  safetyPrecautions: "",
  incidentDuringDisposal: false,
  incidentDetails: "",
  remarks: "",
  attachments: [],
};

const transportMethods = ["Internal", "External", "Courier", "Other"];

const conditions = ["Intact", "Spilled", "Damaged", "Other"];

interface Attachment {
  url: string;
  filename?: string;
  name?: string;
  originalName?: string;
  uploadedAt?: string;
}

const DisposalManagement: React.FC = () => {
  const [rowData, setRowData] = useState<DisposalEventType[]>([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState<DisposalEventType>(emptyForm);
  const [editId, setEditId] = useState<string | null>(null);

  const [files, setFiles] = useState<File[] | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [existingAttachments, setExistingAttachments] = useState<Attachment[]>(
    []
  );
  const [uploadErrorMessage, setUploadErrorMessage] = useState("");
  const [maxFileSize] = useState<number>(10);

  const [fileurl, setFileUrl] = useState({
    url: "",
    name: "",
    type: "",
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isDocumentViewerOpen, setIsDocumentViewerOpen] = useState(false);

  const { getsignedurl } = S3TempUrl();
  const {
    uploadFileToS3,
    progress: uploadProgress,
    uploading,
    error: uploadError,
  } = useS3Upload();

  const {
    disposalEvents,
    loading,
    fetchAllDisposalEvents,
    createDisposalEvent,
    updateDisposalEvent,
    deleteDisposalEvent,
  } = useDisposalManagementStore();

  const {
    disposalMethodsList,
    disposalMethodsListMap,
    loading: loadingDisposalMethodsList,
    fetchAllDisposalMethodsList,
  } = useDisposalMethodsListStore();

  const {
    unitsofMeasure,
    unitsofMeasureMap,
    loading: loadingUnitsOfMeasure,
    fetchAllUnitsofMeasure,
  } = useUnitsofMeasureStore();

  const {
    hazardousMaterials,
    materialDataMap,
    loading: loadingMaterials,
    fetchAllHazardousMaterials,
  } = useHazardousMaterialStore();

  const {
    orgUsersData,
    orgUsersMap,
    loading: loadingOrgUsers,
    fetchOrgUsers,
  } = useOrgUsersStore();

  useEffect(() => {
    fetchAllDisposalEvents();
    if (!hazardousMaterials.length) fetchAllHazardousMaterials();
    if (!disposalMethodsList.length) fetchAllDisposalMethodsList();
    if (!unitsofMeasure.length) fetchAllUnitsofMeasure();
    if (!orgUsersData.length) fetchOrgUsers();
  }, [
    fetchAllDisposalEvents,
    fetchAllDisposalMethodsList,
    fetchAllHazardousMaterials,
    fetchAllUnitsofMeasure,
    fetchOrgUsers,
  ]);
  useEffect(() => {
    setRowData(disposalEvents ?? []);
  }, [disposalEvents, hazardousMaterials, disposalMethodsList, unitsofMeasure]);

  const getFileExtension = (filename: string): string => {
    return filename.split(".").pop()?.toLowerCase() || "";
  };

  const canPreviewFile = (filename: string): boolean => {
    const ext = getFileExtension(filename);
    return [
      "jpg",
      "jpeg",
      "png",
      "pdf",
      "txt",
      "doc",
      "docx",
      "ppt",
      "pptx",
    ].includes(ext);
  };

  const handlePreviewFile = async (attachment: Attachment) => {
    const filename =
      attachment.filename ||
      attachment.name ||
      attachment.originalName ||
      attachment.url?.split("/").pop() ||
      "";

    if (canPreviewFile(filename)) {
      try {
        setIsLoading(true);

        const s3Key = attachment.url?.split("/").pop();
        if (!s3Key) {
          throw new Error("Invalid file URL");
        }

        const fileurl = await getsignedurl(s3Key, getFileExtension(filename));
        const filobj = {
          url: fileurl,
          name: filename,
          type: getFileExtension(filename),
        };
        setFileUrl(filobj);
        setIsDocumentViewerOpen(true);
      } catch (err: any) {
        console.error("Error previewing file:", err);
        setErrorMessage("File not found or failed to load");
        toast.error("Failed to preview file");
      } finally {
        setIsLoading(false);
      }
    } else {
      toast.info(
        "Preview not available for this file type. Use download instead."
      );
    }
  };

  const handleCloseDocumentViewer = () => {
    setIsDocumentViewerOpen(false);
    setFileUrl({ url: "", name: "", type: "" });
    setErrorMessage("");
  };

  const handleRemoveExistingAttachment = (index: number) => {
    if (isEditMode) {
      setExistingAttachments((prev) => {
        const updated = [...prev];
        updated.splice(index, 1);
        return updated;
      });
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;
    if (type === "checkbox" && name === "incidentDuringDisposal") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      if (name === "materialName") {
        const material = hazardousMaterials.find((m) => m._id === value);
        setFormData((prev) => ({
          ...prev,
          [name]: value,
          materialId: material?._id || "",
          unitOfMeasure: material?.unitOfMeasure || "",
        }));
      } else if (name === "quantityDisposed") {
        const material = hazardousMaterials.find(
          (m) => m._id === formData.materialName
        );
        if (material?.quantity && Number(material.quantity) < Number(value)) {
          alert("Selected quantity is greater than the available quantity");
          return;
        } else {
          setFormData((prev) => ({
            ...prev,
            [name]: value,
          }));
        }
      } else {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
    }
  };

  const handleFileChange = async (acceptedFiles: File[]) => {
    const maxTotalSize = maxFileSize * 1024 * 1024;
    const existingFiles = files || [];
    const existingTotalSize = existingFiles.reduce(
      (sum, file) => sum + file.size,
      0
    );

    const newFilesTotalSize = acceptedFiles.reduce(
      (sum, file) => sum + file.size,
      0
    );
    const totalSize = existingTotalSize + newFilesTotalSize;

    if (totalSize > maxTotalSize) {
      toast.error(`Total file size exceeds ${maxFileSize}MB limit.`);
      return;
    }

    const updatedFiles = [...existingFiles, ...acceptedFiles];
    handleFilesChange(updatedFiles);
  };

  const handleFilesChange = (newFiles: File[] | null) => {
    setFiles(newFiles);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleFileChange,
    accept: {
      "application/pdf": [".pdf"],
      "application/msword": [".doc", ".docx"],
      "text/plain": [".txt"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
    },
    disabled: isEditMode || submitting || uploading,
    multiple: true,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const missingFields: string[] = [];
    if (!formData.materialName) missingFields.push("Material Name");
    if (!formData.disposalMethod) missingFields.push("Disposal Method");
    if (!formData.disposalDateTime) missingFields.push("Disposal Date & Time");
    if (!formData.quantityDisposed) missingFields.push("Quantity Disposed");
    if (!formData.authorizedBy) missingFields.push("Authorized By");

    if (missingFields.length > 0) {
      toast.error(
        `Please fill in all required fields: ${missingFields.join(", ")}`
      );
      return;
    }

    if (files && files.length > 0) {
      const maxTotalSize = maxFileSize * 1024 * 1024;
      const totalSize = files.reduce((sum, file) => sum + file.size, 0);

      if (totalSize > maxTotalSize) {
        toast.error(
          `Total file size exceeds ${maxFileSize}MB limit. Please remove some files.`
        );
        setUploadErrorMessage(
          "Total file size exceeds 10MB limit. Please remove some files."
        );
        return;
      }
    }

    setSubmitting(true);
    setUploadErrorMessage("");

    let uploadedFileObjects: Array<{
      url: string;
      name: string;
      uploadedAt: string;
    }> = [];

    try {
      if (files && files.length > 0) {
        for (const file of files) {
          const s3Url = await uploadFileToS3(file);
          if (s3Url) {
            uploadedFileObjects.push({
              url: s3Url,
              name: file.name,
              uploadedAt: new Date().toISOString(),
            });
          } else {
            throw new Error(`Failed to upload file: ${file.name}`);
          }
        }
      }
    } catch (error) {
      setUploadErrorMessage(`File upload failed: ${(error as Error).message}`);
      setSubmitting(false);
      return;
    }

    try {
      const existingAttachmentObjects = existingAttachments.map((a) => ({
        url: a.url || (typeof a === "string" ? a : ""),
        name:
          a.filename ||
          a.name ||
          a.originalName ||
          a.url?.split("/").pop() ||
          "Unknown file",
        uploadedAt: a.uploadedAt || new Date().toISOString(),
      }));

      const payload = {
        ...formData,
        attachments: [...existingAttachmentObjects, ...uploadedFileObjects],
      };

      let success;
      if (isEditMode && editId) {
        success = await updateDisposalEvent(payload, editId);
      } else {
        success = await createDisposalEvent(payload);
      }

      if (success) {
        const successMessage = isEditMode
          ? "Disposal event updated successfully"
          : "Disposal event created successfully";
        toast.success(successMessage);

        setModalOpen(false);
        setFormData(emptyForm);
        setEditId(null);
        setIsEditMode(false);
        setFiles(null);
        setExistingAttachments([]);
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        (isEditMode
          ? "Failed to update disposal event"
          : "Failed to create disposal event");
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (data: DisposalEventType) => {
    setIsEditMode(true);

    let attachmentsArray: Attachment[] = [];
    if (data.attachments && Array.isArray(data.attachments)) {
      attachmentsArray = data.attachments.map((attachment: any) => {
        if (typeof attachment === "string") {
          return {
            url: attachment,
            filename: attachment.split("/").pop() || "Unknown file",
            uploadedAt: new Date().toISOString(),
          };
        } else {
          return {
            url: attachment.url || attachment,
            filename:
              attachment.filename ||
              attachment.name ||
              attachment.originalName ||
              attachment.url?.split("/").pop() ||
              "Unknown file",
            uploadedAt: attachment.uploadedAt || new Date().toISOString(),
          };
        }
      });
    }

    setFormData({
      ...data,
      disposalDateTime: data.disposalDateTime
        ? new Date(data.disposalDateTime).toISOString().slice(0, 16)
        : "",
    });

    setExistingAttachments(attachmentsArray);
    setEditId(data._id || "");
    setModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (
      window.confirm("Are you sure you want to delete this disposal event?")
    ) {
      await deleteDisposalEvent(id);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setFormData(emptyForm);
    setEditId(null);
    setIsEditMode(false);
    setFiles(null);
    setExistingAttachments([]);
  };

  const isDisabled = submitting || uploading;

  const columnDefs = useMemo<any[]>(
    () => [
      {
        field: "disposalDateTime",
        headerName: "Date & Time",
        minWidth: 160,
        headerStyle: { backgroundColor: "#e9f5ff" },
        valueGetter: (params: any) =>
          new Date(params.data.disposalDateTime).toLocaleString("en-US", {
            year: "numeric",
            month: "numeric",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          }),
      },
      {
        field: "materialName",
        headerName: "Material Name",
        minWidth: 120,
        headerStyle: { backgroundColor: "#e9f5ff" },
        valueGetter: (params: any) =>
          materialDataMap.get(params.data.materialName) || "N/A",
      },
      {
        field: "quantityDisposed",
        headerName: "Qty",
        minWidth: 80,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: { textAlign: "left", paddingLeft: 10 },
      },
      {
        field: "unitOfMeasure",
        headerName: "Unit",
        minWidth: 80,
        headerStyle: { backgroundColor: "#e9f5ff" },
        valueGetter: (params: any) =>
          unitsofMeasureMap.get(params.data.unitOfMeasure) || "N/A",
      },
      {
        field: "disposalMethod",
        headerName: "Method",
        minWidth: 130,
        headerStyle: { backgroundColor: "#e9f5ff" },
        valueGetter: (params: any) =>
          disposalMethodsListMap.get(params.data.disposalMethod) || "N/A",
      },
      {
        field: "authorizedBy",
        headerName: "Authorized By",
        minWidth: 100,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: { padding: "5px 0 5px 5px", textAlign: "left" },
        valueGetter: (params: any) =>
          orgUsersMap.get(params.data.authorizedBy) || "N/A",
      },
      {
        field: "action",
        headerName: "Action",
        minWidth: 90,
        maxWidth: 110,
        cellStyle: { padding: "10px 15px 0 0", textAlign: "left" },
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellRenderer: (params: any) => (
          <div className="flex gap-x-3 pt-2">
            <button
              onClick={() => handleEdit(params.data)}
              className="p-1 hover:bg-gray-100 rounded"
              title="Edit"
            >
              <img
                className="iconTd w-5 h-5"
                src={images.editIcon}
                alt="edit"
              />
            </button>
            <button
              onClick={() => handleDelete(params.data._id)}
              className="p-1 hover:bg-red-100 rounded"
              title="Delete"
            >
              <Trash2 className="text-red-500" size={23} />
            </button>
          </div>
        ),
      },
    ],
    [materialDataMap, orgUsersMap, unitsofMeasureMap, disposalMethodsListMap]
  );

  const defaultColDef = useMemo<any>(() => ({ editable: false }), []);
  const theme = useMemo<any>(() => gridTheme, []);

  return (
    <>
      {/* Add loading overlay */}
      {(uploading || submitting) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full text-center">
            <div className="flex justify-center mb-4">
              <svg
                className="animate-spin h-8 w-8 text-teal-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
            <h3 className="text-lg font-medium mb-2">
              {uploading ? "Uploading files..." : "Submitting..."}
            </h3>
            {uploading && (
              <p className="text-sm text-gray-600">
                Progress: {uploadProgress}%
              </p>
            )}
          </div>
        </div>
      )}

      <div className="px-2 py-4 mt-14 min-h-screen">
        <div className="flex justify-between items-center mb-4">
          <h2 className="ml-2">Disposal Management</h2>
          <button
            className="flex items-center justify-center font-semibold gap-2 bg-[#0fa9a6] text-white border border-[#0fa9a6] px-4 py-2 rounded-full hover:bg-teal-600"
            onClick={() => {
              setModalOpen(true);
              setIsEditMode(false);
              setFormData(emptyForm);
              setExistingAttachments([]);
              setFiles(null);
            }}
          >
            <Plus size={20} /> Add Disposal Event
          </button>
        </div>

        <div className="tableBlock w-full">
          <div className="card">
            {rowData?.length > 0 ? (
              <div className="ag-theme-alpine w-full min-h-[400px] rounded-lg shadow-lg">
                <AgGridReact
                  rowData={rowData}
                  columnDefs={columnDefs}
                  theme={theme}
                  headerHeight={65}
                  rowHeight={50}
                  loading={loading}
                  defaultColDef={defaultColDef}
                  domLayout="autoHeight"
                  pagination
                  paginationPageSize={5}
                />
              </div>
            ) : (
              <div className="flex flex-col justify-center items-center h-64">
                <p className="text-gray-500 text-lg mb-4">
                  No disposal events found
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        closeButton={false}
        modalClass="bg-white w-full max-w-4xl rounded-xl shadow-lg relative max-h-[80vh] flex flex-col"
      >
        <div className="pt-6 px-6 flex-1 overflow-y-auto">
          <h2 className="text-xl font-semibold text-gray-800 border-b pb-2 mb-6">
            {isEditMode ? "Update Disposal Event" : "Add Disposal Event"}
          </h2>
          <form
            onSubmit={handleSubmit}
            id="disposalEventForm"
            className="flex flex-col gap-6 pb-10"
          >
            {/* Disposal Event Details */}
            <div className="grid grid-cols-2 gap-6">
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="materialName"
                  className="text-sm font-medium text-gray-700"
                >
                  Material Name<span className="text-red-500">*</span>
                </label>
                <select
                  name="materialName"
                  value={formData.materialName}
                  onChange={handleChange}
                  required
                  disabled={isDisabled}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                >
                  <option value="">Select Material</option>
                  {hazardousMaterials?.map((item) => (
                    <option key={item._id} value={item._id}>
                      {item.materialName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="disposalMethod"
                  className="text-sm font-medium text-gray-700"
                >
                  Disposal Method<span className="text-red-500">*</span>
                </label>
                <select
                  id="disposalMethod"
                  name="disposalMethod"
                  value={formData.disposalMethod}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  aria-label="Disposal Method"
                  required
                  disabled={isDisabled}
                >
                  <option value="">Select</option>
                  {disposalMethodsList.map((m) => (
                    <option key={m._id} value={m._id}>
                      {m.methodName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="disposalDateTime"
                  className="text-sm font-medium text-gray-700"
                >
                  Disposal Date & Time<span className="text-red-500">*</span>
                </label>
                <input
                  type="datetime-local"
                  id="disposalDateTime"
                  name="disposalDateTime"
                  value={formData.disposalDateTime}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  aria-label="Disposal Date and Time"
                  required
                  disabled={isDisabled}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="quantityDisposed"
                  className="text-sm font-medium text-gray-700"
                >
                  Quantity Disposed<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="quantityDisposed"
                  name="quantityDisposed"
                  value={formData.quantityDisposed}
                  title="Enter available quantity"
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  placeholder="Enter amount"
                  aria-label="Quantity Disposed"
                  required
                  disabled={isDisabled}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="unitOfMeasure"
                  className="text-sm font-medium text-gray-700"
                >
                  Unit of Measure
                </label>
                <select
                  id="unitOfMeasure"
                  name="unitOfMeasure"
                  value={formData.unitOfMeasure}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  disabled={isDisabled}
                >
                  <option value="">Select</option>
                  {unitsofMeasure?.map((u) => (
                    <option key={u._id} value={u._id}>
                      {u.unitSymbol}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="reasonForDisposal"
                  className="text-sm font-medium text-gray-700"
                >
                  Reason for Disposal
                </label>
                <input
                  type="text"
                  id="reasonForDisposal"
                  name="reasonForDisposal"
                  value={formData.reasonForDisposal}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  placeholder="Expired, surplus, contamination, etc."
                  aria-label="Reason for Disposal"
                  disabled={isDisabled}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="conditionBeforeDisposal"
                  className="text-sm font-medium text-gray-700"
                >
                  Condition Before Disposal
                </label>
                <select
                  id="conditionBeforeDisposal"
                  name="conditionBeforeDisposal"
                  value={formData.conditionBeforeDisposal}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  aria-label="Condition Before Disposal"
                  disabled={isDisabled}
                >
                  <option value="">Select</option>
                  {conditions.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-2">
                <label
                  htmlFor="disposalVendor"
                  className="text-sm font-medium text-gray-700"
                >
                  Disposal Vendor / Agency
                </label>
                <input
                  type="text"
                  id="disposalVendor"
                  name="disposalVendor"
                  value={formData.disposalVendor}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  placeholder="Vendor or agency name"
                  aria-label="Disposal Vendor"
                  disabled={isDisabled}
                />
              </div>

              <div className="flex flex-col gap-2">
                <label
                  htmlFor="authorizedBy"
                  className="text-sm font-medium text-gray-700"
                >
                  Authorized By<span className="text-red-500">*</span>
                </label>
                <select
                  id="authorizedBy"
                  name="authorizedBy"
                  value={formData.authorizedBy}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  required
                  disabled={isDisabled}
                >
                  <option value="">Select Authorized By</option>
                  {orgUsersData.map((u: OrgUserType) => (
                    <option key={u._id as string} value={u._id as string}>
                      {u.firstName} {u.lastName} - {u.email}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-2 col-span-2">
                <label
                  htmlFor="safetyPrecautions"
                  className="text-sm font-medium text-gray-700"
                >
                  Safety Precautions Followed
                </label>
                <textarea
                  id="safetyPrecautions"
                  name="safetyPrecautions"
                  value={formData.safetyPrecautions}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none resize-y"
                  placeholder="Safety steps taken"
                  aria-label="Safety Precautions"
                  rows={2}
                  disabled={isDisabled}
                />
              </div>

              <div className="flex flex-col gap-2 col-span-2">
                <label
                  htmlFor="remarks"
                  className="text-sm font-medium text-gray-700"
                >
                  Remarks / Notes
                </label>
                <textarea
                  id="remarks"
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none resize-y"
                  placeholder="Additional notes"
                  aria-label="Remarks"
                  rows={2}
                  disabled={isDisabled}
                />
              </div>
            </div>

            <FileUpload
              mode={isEditMode ? "update" : "create"}
              files={files}
              onFilesChange={handleFilesChange}
              existingAttachments={existingAttachments}
              onRemoveExistingAttachment={handleRemoveExistingAttachment}
              onPreviewFile={handlePreviewFile}
              uploading={uploading}
              uploadProgress={uploadProgress}
              disabled={isDisabled}
              maxFileSizeMB={maxFileSize}
              getRootProps={getRootProps}
              getInputProps={getInputProps}
            />

            <div className="h-10" />
          </form>
        </div>
        <div className="flex justify-end gap-3 p-3 bg-[#5A7192] w-full rounded-b-lg sticky bottom-0">
          <button
            type="button"
            className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-teal-600 transition"
            onClick={handleModalClose}
            disabled={isDisabled}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="disposalEventForm"
            className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
            disabled={isDisabled}
          >
            {isDisabled ? "Submitting..." : isEditMode ? "Update" : "Add"}
          </button>
        </div>
      </Modal>

      {/* Document Viewer Modal - Add this */}
      {isDocumentViewerOpen && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black bg-opacity-45">
          <div className="relative bg-white rounded-lg shadow-xl w-[90vw] h-[90vh] flex flex-col">
            <div className="absolute right-4 top-4 z-10">
              <button
                onClick={handleCloseDocumentViewer}
                className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-full shadow-lg transition-colors duration-200"
                aria-label="Close document viewer"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="flex-1 p-4 overflow-auto">
              {errorMessage && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  {errorMessage}
                </div>
              )}
              {isLoading ? (
                <div className="flex justify-center items-center h-full">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : fileurl.url ? (
                fileurl.type === "pdf" ? (
                  <div className="w-full h-full">
                    <PdfViewer fileUrl={fileurl.url} />
                  </div>
                ) : (
                  <div className="w-full h-full">
                    <DocumentViewer file={fileurl.url} name={fileurl.name} />
                  </div>
                )
              ) : (
                <div className="flex justify-center items-center h-full">
                  <p>No document to display</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DisposalManagement;
